import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Search, ChevronDown, X, Loader2, Check, RotateCcw } from 'lucide-react';
import { useVendorList } from '../hooks/useVendorList';
import { useVendorColors } from '../hooks/useVendorColors';
import { vendorMatchesQuery } from '../../../lib/vendorCoverage';
import './VendorOverlayPicker.css';

/**
 * Multi-select vendor picker for the ZT framework overlay.
 *
 * Props:
 *   selectedVendors — array of enhanced vendor objects (color, name, id),
 *     one per currently-active overlay. Empty array = no overlay.
 *   onToggle — (vendorId: number) => void. Adds or removes a vendor.
 *   onClear — () => void. Drops all overlays.
 */
export default function VendorOverlayPicker({ selectedVendors = [], onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch all vendors once; filter client-side for snappier typing.
  const { data: vendors = [], isLoading, error } = useVendorList();

  // Live color overrides (persisted per-user in localStorage).
  const { colorFor, hasOverride, setColor, resetColor } = useVendorColors();

  // O(1) membership check for the rendered list.
  const selectedIds = useMemo(
    () => new Set(selectedVendors.map(v => v.id)),
    [selectedVendors]
  );

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Focus search when dropdown opens.
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    } else if (!open) {
      setQuery('');
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query) return vendors;
    return vendors.filter(v => vendorMatchesQuery(v, query));
  }, [vendors, query]);

  const handleClear = (e) => {
    e.stopPropagation();
    onClear?.();
  };

  const hasSelection = selectedVendors.length > 0;

  return (
    <div className="vop-container" ref={containerRef}>
      <button
        type="button"
        className={`vop-trigger ${hasSelection ? 'vop-trigger--has-selection' : ''}`}
        onClick={() => setOpen(o => !o)}
        title={
          hasSelection
            ? `Overlay active for ${selectedVendors.length} vendor(s). Click to edit.`
            : 'Highlight where vendors\' products cover the Zero Trust framework'
        }
      >
        {hasSelection ? (
          <>
            <span className="vop-trigger-dots">
              {selectedVendors.slice(0, 5).map(v => (
                <span
                  key={v.id}
                  className="vop-trigger-dot"
                  style={{ background: colorFor(v) }}
                  title={v.name}
                />
              ))}
              {selectedVendors.length > 5 && (
                <span className="vop-trigger-dot-more">+{selectedVendors.length - 5}</span>
              )}
            </span>
            <span className="vop-trigger-label">
              {selectedVendors.length === 1
                ? selectedVendors[0].name
                : `${selectedVendors.length} vendors`}
            </span>
            <span
              className="vop-trigger-clear"
              onClick={handleClear}
              role="button"
              aria-label="Clear all vendor overlays"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClear(e); }}
            >
              <X size={12} />
            </span>
          </>
        ) : (
          <>
            <Search size={14} />
            <span>Highlight vendor</span>
            <ChevronDown size={12} className={open ? 'vop-chevron-rotated' : ''} />
          </>
        )}
      </button>

      {open && (
        <div className="vop-dropdown" role="listbox" aria-multiselectable="true">
          <div className="vop-search">
            <Search size={14} className="vop-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="vop-search-input"
              placeholder="Search vendors (Microsoft, Palo Alto, Elastic…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {isLoading && (
            <div className="vop-status">
              <Loader2 size={14} className="spin" /> Loading vendors…
            </div>
          )}

          {error && !isLoading && (
            <div className="vop-status vop-status--error">
              Couldn't load vendors: {error.message || 'unknown error'}
            </div>
          )}

          {!isLoading && !error && filtered.length === 0 && (
            <div className="vop-status vop-status--empty">
              {query ? `No vendors match "${query}"` : 'No vendors available'}
            </div>
          )}

          {!isLoading && !error && filtered.length > 0 && (
            <>
              <ul className="vop-list">
                {filtered.map(vendor => {
                  const isSelected = selectedIds.has(vendor.id);
                  const color = colorFor(vendor);
                  const vendorHasOverride = hasOverride(vendor);
                  return (
                    <li key={vendor.id} className="vop-row">
                      <button
                        type="button"
                        className={`vop-option ${isSelected ? 'vop-option--selected' : ''}`}
                        onClick={() => onToggle?.(vendor.id)}
                        role="option"
                        aria-selected={isSelected}
                      >
                        <span className="vop-option-check">
                          {isSelected && <Check size={12} />}
                        </span>
                        <span
                          className="vop-option-dot"
                          style={{ background: color }}
                        />
                        <span className="vop-option-name">{vendor.name}</span>
                        <span className="vop-option-count">
                          {vendor.verifiedProductCount} cap{vendor.verifiedProductCount === 1 ? '' : 's'}
                        </span>
                      </button>
                      <div className="vop-color-controls">
                        <label
                          className="vop-color-edit"
                          title={`Change ${vendor.name}'s color`}
                          aria-label={`Change ${vendor.name}'s color`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span
                            className="vop-color-swatch"
                            style={{ background: color }}
                          />
                          <input
                            type="color"
                            className="vop-color-input"
                            value={color}
                            onChange={(e) => setColor(vendor, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            aria-hidden="true"
                            tabIndex={-1}
                          />
                        </label>
                        {vendorHasOverride && (
                          <button
                            type="button"
                            className="vop-color-reset"
                            onClick={(e) => { e.stopPropagation(); resetColor(vendor); }}
                            title={`Reset ${vendor.name} to default color`}
                            aria-label={`Reset ${vendor.name} to default color`}
                          >
                            <RotateCcw size={11} />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {hasSelection && (
                <div className="vop-footer">
                  <button type="button" className="vop-clear-all" onClick={onClear}>
                    Clear all ({selectedVendors.length})
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
